import type { HeroContent } from '@/types/contentblocks'
import type { HeroSlideItem, HeroSlideResolvedEvent, HeroSlideLink } from '@/types/heroSlider'

type HeroSlide = HeroContent['slides'][number]
type HeroSlideImage = HeroSlide['content']['imageObject']
type CubeSlide = HeroSlide | HeroSlideItem
type CubeLinkObject = HeroSlideLink | null

const MOBILE_QUERY = '(max-width: 768px)'
// how far .cube tilts at rest so .left reads as a distinct side face next to .front. Mobile uses
// a shallower angle than desktop; fitCubeWidth() compensates for the fan-out this causes
const REST_TILT_DESKTOP = 38
const REST_TILT_MOBILE = 25

const SPIN_DURATION_DESKTOP_MS = 1600
const SPIN_DURATION_MOBILE_MS = 1200

interface UseHeroCubeCarouselOptions {
  slides: Ref<CubeSlide[]>
  wrapperRef: Ref<HTMLElement | null>
  carouselRef: Ref<HTMLElement | null>
  sceneRef: Ref<HTMLElement | null>
  cubeRef: Ref<HTMLElement | null>
  frontRef: Ref<HTMLElement | null>
  backRef: Ref<HTMLElement | null>
  leftRef: Ref<HTMLElement | null>
  rightRef: Ref<HTMLElement | null>
  prevBtnRef: Ref<HTMLElement | null>
  nextBtnRef: Ref<HTMLElement | null>
}

export function useHeroCubeCarousel(options: UseHeroCubeCarouselOptions) {
  const {
    slides,
    wrapperRef,
    carouselRef,
    sceneRef,
    cubeRef,
    frontRef,
    backRef,
    leftRef,
    rightRef,
    prevBtnRef,
    nextBtnRef,
  } = options

  const currentIndex = ref(0)
  const visiblePair = ref(0) // 0 = front+left is facing the viewer, 1 = back+right is
  const isMobile = ref(false)
  const isAnimating = ref(false)

  function getSlideTitle(slide?: CubeSlide) {
    if (!slide?.content) return ''
    const event = (slide as HeroSlideItem).event as HeroSlideResolvedEvent
    const eventTitle = event?.title
    return slide.content.title || eventTitle || ''
  }

  function getSlideImage(slide?: CubeSlide): HeroSlideImage | null {
    if (!slide?.content) return null
    const event = (slide as HeroSlideItem).event as HeroSlideResolvedEvent
    const eventImage: HeroSlideImage = event?.image1
    return slide.content.imageObject?.id ? slide.content.imageObject : eventImage || null
  }

  function getSlideIntro(slide?: CubeSlide) {
    if (!slide?.content) return ''
    const event = (slide as HeroSlideItem).event as HeroSlideResolvedEvent
    const eventIntro = event?.subTitle

    return slide.content.intro || eventIntro || ''
  }

  function getSlideSubtitle(slide?: CubeSlide) {
    if (!slide?.content) return ''
    const event = (slide as HeroSlideItem).event as HeroSlideResolvedEvent
    const eventStartAt = event?.programs?.[0]?.startAt
    const eventSubTitle = eventStartAt ? formatDateNumeric(eventStartAt) : ''

    return slide.content.subtitle || eventSubTitle || ''
  }

  function getSlideLink(slide?: CubeSlide): CubeLinkObject {
    if (!slide?.content) return null
    const event = (slide as HeroSlideItem).event as HeroSlideResolvedEvent
    const eventLink: CubeLinkObject = event?.slug
      ? {
          name: 'Bekijk deze voorstelling',
          link: `/programma/${event.slug}`,
          target: '_self',
        }
      : null

    return slide.content.linkObject1?.name ? slide.content.linkObject1 : eventLink || null
  }

  const firstSlide = slides.value[0]
  const frontTitle = ref(getSlideTitle(firstSlide))
  const frontSubtitle = ref(getSlideSubtitle(firstSlide))
  const leftImage = ref<HeroSlideImage | null>(getSlideImage(firstSlide))
  const leftImageVersion = ref(0)
  const frontIntro = ref(getSlideIntro(firstSlide))
  const frontLinkObject = ref<CubeLinkObject>(getSlideLink(firstSlide))
  const backTitle = ref('')
  const backSubtitle = ref('')
  const backIntro = ref('')
  const backLinkObject = ref<CubeLinkObject>(null)
  const rightImage = ref<HeroSlideImage | null>(null)
  const rightImageVersion = ref(0)

  let rotateY = REST_TILT_DESKTOP
  let shiftX = 0
  let resizeObserver: ResizeObserver | null = null
  let mobileQuery: MediaQueryList | null = null

  function applyRotation() {
    if (!cubeRef.value) return
    cubeRef.value.style.transform = `translateX(${shiftX}px) rotateY(${rotateY}deg)`
  }

  // PlaySpin is needed for Safari as it rotates to different directions when rotateY() is 180°

  const SPIN_AMBIGUITY_NUDGE_DEG = 0.5

  function playSpin(fromDeg: number, toDeg: number, durationMs: number) {
    const cube = cubeRef.value
    if (!cube) return

    const delta = toDeg - fromDeg
    const earlyDeg = fromDeg + Math.sign(delta) * SPIN_AMBIGUITY_NUDGE_DEG

    cube.style.setProperty('--spin-shift', `${shiftX}px`)
    cube.style.setProperty('--spin-from', `${fromDeg}deg`)
    cube.style.setProperty('--spin-early', `${earlyDeg}deg`)
    cube.style.setProperty('--spin-to', `${toDeg}deg`)

    cube.style.animation = 'none'
    void cube.offsetHeight // restart the animation even if the previous spin used the same keyframes
    cube.style.animation = `cube-spin ${durationMs}ms var(--ease-fast-in-slow-out) forwards`
  }

  // .cube animates its transform, so reading getBoundingClientRect() right after changing it
  // would measure a mid-transition position instead of the real resting one
  function withoutTransition(change: () => void) {
    const cube = cubeRef.value
    if (!cube) {
      change()
      return
    }

    cube.style.transition = 'none'
    change()
    const _forceReflow = cube.offsetHeight // commits the change above before restoring the transition
    cube.style.transition = ''
  }

  // at a wide enough tilt, .front and .left fan out to together span more than --box-width — by
  // a fixed ratio, since --box-depth/--box-perspective are themselves defined as ratios of
  // --box-width, so shrinking --box-width can never change how much wider the content is *than
  // the box itself*. What it CAN do is shrink the box (and its proportional fan-out) until the
  // fanned-out content fits the actual available width, which is .carousel's own width — that
  // one does NOT depend on --box-width, so it works as a stable target to size against
  function fitCubeWidth() {
    if (!carouselRef.value || !sceneRef.value) return

    const carousel = carouselRef.value
    carousel.style.removeProperty('--box-width') // always measure the CSS-defined width, not a previous shrink

    if (!isMobile.value) return

    const textEl = visiblePair.value === 0 ? frontRef.value : backRef.value
    const imageEl = visiblePair.value === 0 ? leftRef.value : rightRef.value
    if (!textEl || !imageEl) return

    const availableWidth = carousel.clientWidth
    const naturalBoxWidth = sceneRef.value.getBoundingClientRect().width
    const textRect = textEl.getBoundingClientRect()
    const imageRect = imageEl.getBoundingClientRect()
    const contentWidth = Math.max(textRect.right, imageRect.right) - Math.min(textRect.left, imageRect.left)

    if (!naturalBoxWidth || contentWidth <= 0) return

    const fanOutRatio = contentWidth / naturalBoxWidth
    if (fanOutRatio <= 1 || contentWidth <= availableWidth) return

    const newBoxWidth = availableWidth / fanOutRatio
    carousel.style.setProperty('--box-width', `${newBoxWidth}px`)
  }

  // the perspective tilt projects .cube's faces asymmetrically (one side bulges more than the other),
  // so we measure the actual overflow against .scene to cancel it out with translateX
  function centerCube() {
    if (!sceneRef.value) return

    // .cube itself is never bigger than .scene — it's the .face children (positioned via 3D
    // transforms) that actually bulge outside of it, so measure the faces that are visible now
    const textEl = visiblePair.value === 0 ? frontRef.value : backRef.value
    const imageEl = visiblePair.value === 0 ? leftRef.value : rightRef.value
    if (!textEl || !imageEl) return

    const sceneRect = sceneRef.value.getBoundingClientRect()
    const textRect = textEl.getBoundingClientRect()
    const imageRect = imageEl.getBoundingClientRect()
    const contentLeft = Math.min(textRect.left, imageRect.left)
    const contentRight = Math.max(textRect.right, imageRect.right)
    const overflowLeft = sceneRect.left - contentLeft
    const overflowRight = contentRight - sceneRect.right
    const correction = (overflowLeft - overflowRight) / 2

    shiftX += correction
    applyRotation()
  }

  function updateArrowPositions() {
    if (!prevBtnRef.value || !nextBtnRef.value) return

    if (isMobile.value) {
      prevBtnRef.value.style.removeProperty('top')
      prevBtnRef.value.style.removeProperty('left')
      prevBtnRef.value.style.removeProperty('right')
      nextBtnRef.value.style.removeProperty('top')
      nextBtnRef.value.style.removeProperty('left')
      nextBtnRef.value.style.removeProperty('right')
      return
    }

    if (!wrapperRef.value || !sceneRef.value) return

    const imageEl = visiblePair.value === 0 ? leftRef.value : rightRef.value
    const textEl = visiblePair.value === 0 ? frontRef.value : backRef.value
    if (!imageEl || !textEl) return

    const wrapperRect = wrapperRef.value.getBoundingClientRect()
    const sceneRect = sceneRef.value.getBoundingClientRect()
    const imageRect = imageEl.getBoundingClientRect()
    const textRect = textEl.getBoundingClientRect()
    const btnSize = prevBtnRef.value.offsetWidth
    const top = sceneRect.top - wrapperRect.top + sceneRect.height / 2
    const prevLeft = imageRect.left - wrapperRect.left - btnSize / 2
    const nextLeft = textRect.right - wrapperRect.left - btnSize / 2

    prevBtnRef.value.style.top = `${top}px`
    nextBtnRef.value.style.top = `${top}px`

    prevBtnRef.value.style.left = `${prevLeft}px`
    prevBtnRef.value.style.right = 'auto'

    nextBtnRef.value.style.left = `${nextLeft}px`
    nextBtnRef.value.style.right = 'auto'
  }

  function syncOrientation(mobile: boolean) {
    isMobile.value = mobile
    const tilt = mobile ? REST_TILT_MOBILE : REST_TILT_DESKTOP
    rotateY = tilt + (visiblePair.value === 1 ? 180 : 0)
    settle()
  }

  function go(direction: number) {
    if (!slides.value.length || isAnimating.value) return

    currentIndex.value = (currentIndex.value + direction + slides.value.length) % slides.value.length
    const hiddenPair = visiblePair.value === 0 ? 1 : 0
    const slide = slides.value[currentIndex.value]
    if (!slide?.content) return

    isAnimating.value = true

    if (hiddenPair === 0) {
      frontTitle.value = getSlideTitle(slide)
      frontSubtitle.value = getSlideSubtitle(slide)
      leftImage.value = getSlideImage(slide)
      leftImageVersion.value += 1
      frontIntro.value = getSlideIntro(slide)
      frontLinkObject.value = getSlideLink(slide)
    } else {
      backTitle.value = getSlideTitle(slide)
      backSubtitle.value = getSlideSubtitle(slide)
      rightImage.value = getSlideImage(slide)
      rightImageVersion.value += 1
      backIntro.value = getSlideIntro(slide)
      backLinkObject.value = getSlideLink(slide)
    }

    const fromDeg = rotateY
    rotateY += direction === 1 ? -180 : 180
    const spinDuration = isMobile.value ? SPIN_DURATION_MOBILE_MS : SPIN_DURATION_DESKTOP_MS
    playSpin(fromDeg, rotateY, spinDuration)

    visiblePair.value = hiddenPair
    setTimeout(() => {
      if (cubeRef.value) cubeRef.value.style.animation = ''
      settle()
      isAnimating.value = false
    }, spinDuration)
  }

  function settle() {
    withoutTransition(() => {
      fitCubeWidth()
      shiftX = 0
      applyRotation()
      centerCube()
    })
    updateArrowPositions()
  }

  function handleMobileChange(event: MediaQueryListEvent) {
    syncOrientation(event.matches)
  }

  onMounted(() => {
    mobileQuery = window.matchMedia(MOBILE_QUERY)
    mobileQuery.addEventListener('change', handleMobileChange)
    syncOrientation(mobileQuery.matches)
    setTimeout(settle, 600)

    if (!sceneRef.value) return
    resizeObserver = new ResizeObserver(settle)
    resizeObserver.observe(sceneRef.value)
  })

  onBeforeUnmount(() => {
    mobileQuery?.removeEventListener('change', handleMobileChange)
    resizeObserver?.disconnect()
  })

  return {
    frontTitle,
    frontSubtitle,
    leftImage,
    leftImageVersion,
    frontIntro,
    frontLinkObject,
    backTitle,
    backSubtitle,
    backIntro,
    backLinkObject,
    rightImage,
    rightImageVersion,
    isAnimating,
    go,
  }
}
